import classNames from "classnames"

import AddToCartButton from "@components/elements/V2/Buttons/AddToCartButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import PriceDisplay from "@components/elements/V2/PriceDisplay"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import {
  isConsolidatedPurchasableItem,
  useGetConsolidatedItem,
} from "@hooks/V2/storyblok/useConsolidatedItem"
import {
  storyblokItemFromConfig,
  useGetStoryblokItem,
} from "@hooks/V2/storyblok/useItem"
import { itemAlreadyInCart } from "@utils/shop"
import { isMobile } from "@utils/V2/screen"
import React, { useRef, useState } from "react"

type Props = {
  product: Storyblok.Product | Storyblok.Bundle | string
  className?: string
}

const RecommendedProductCard = ({ product, className }: Props) => {
  const getStoryblokItem = useGetStoryblokItem()
  const storyblokItem = getStoryblokItem(product)
  const isOnMobile = isMobile()

  const [onHover, setOnHover] = useState(false)
  const addButtonRef = useRef<HTMLDivElement>(null)

  const handleCardClick = () => {
    addButtonRef.current?.querySelector("button")?.click()
  }

  const getConsolidatedItem = useGetConsolidatedItem()
  const config = storyblokItem.content
  const consolidatedItem = config
    ? getConsolidatedItem(storyblokItemFromConfig(storyblokItem.id, config))
    : undefined

  if (
    !storyblokItem.content ||
    !isConsolidatedPurchasableItem(consolidatedItem)
  )
    return null

  if (itemAlreadyInCart(consolidatedItem)) return null

  const imageFile = consolidatedItem.thumbnail?.filename ?? ""
  const imageAlt = consolidatedItem.thumbnail?.alt ?? ""

  return (
    <div
      onMouseEnter={() => setOnHover(true)}
      onMouseLeave={() => setOnHover(false)}
      onClick={handleCardClick}
      className={classNames(
        "group mb-16 flex cursor-pointer gap-16 rounded-16 border border-charcoal/10 bg-white p-16 transition-colors duration-150 sm:p-20",
        onHover && "!border-blue",
        className
      )}
    >
      <div className="flex w-full gap-10">
        <GatsbyStoryblokImage
          alt={imageAlt || "Product image"}
          aspectRatio="1:1"
          image={imageFile}
          className="size-[48px] shrink-0 md:size-[72px]"
        />

        <div className="my-auto shrink-0">
          <Typography
            text={consolidatedItem.name}
            size="body-md"
            font="grotesk"
            weight="medium"
            color={Color.Black}
            className="text-wrap"
          />

          <PriceDisplay
            originalPriceColor={Color.Orange}
            onPromotion={consolidatedItem.onPromotion}
            productPrices={{
              price: consolidatedItem.price || "0",
              originalPrice: consolidatedItem.originalPrice,
            }}
            originalPricePosition="right"
            priceFontSize="body-lg"
            superscriptFontsize="body-sm"
            priceFontWeight="book"
          />
        </div>
      </div>

      <div ref={addButtonRef} onClick={(e) => e.stopPropagation()}>
        <AddToCartButton
          purchasableItem={consolidatedItem}
          size="medium"
          style="solid"
          buttonColor={onHover ? Color.Blue : Color.Black}
          buttonText="Add"
          openCartPreview={false}
          iconSize={10}
          iconStrokeWidth={3}
          hideIcon={isOnMobile}
          className="my-auto ml-auto w-max gap-4 rounded-8 border-opacity-55 !p-8 transition-colors duration-150"
          isRecommendedProduct
        />
      </div>
    </div>
  )
}

export default RecommendedProductCard
